// src/context/AuthContext.js
import React, { createContext, useContext, useState, useEffect, useRef, useMemo, useCallback } from 'react';
import { auth, db } from '../services/firebase';
import {
  signInWithEmailAndPassword,
  signOut,
  onIdTokenChanged
} from 'firebase/auth';
import { doc, getDoc } from 'firebase/firestore';
import axiosInstance, { clearApiCache } from '../services/api';
import { supportSessionOrgId } from '../utils/supportSession';
import { isViewingAs, viewAsSession, viewAsHeaders, forgetViewAs } from '../utils/viewAs';
import { setFrozenDate } from '../utils/dateConfig';
import { companyDashboardEnabled, companySelection, companyScopeHeaders, clearCompanySelection,
  prepareCompanySwitch, fetchCompanyAccess, isSessionEvicted, autoOpenTarget } from '../services/companyDashboardScope';
import { buildCompanyEntries, readSiteGroup, writeSiteGroup, clearSiteGroup } from '../utils/companyEntries';
import { sortByName } from '../utils/sortByName';

const AuthContext = createContext();

// Mirrors the axios LOGGED_IN_ELSEWHERE handler in services/api.js, plus the
// Firebase sign-out: company-access requests bypass axios, and without ending
// the Firebase session the auth listener would keep re-running from /login.
async function signOutEvictedSession() {
  sessionStorage.clear();
  localStorage.removeItem('impersonation_session');
  clearApiCache();
  await signOut(auth);
  window.location.assign('/login');
}

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) throw new Error('useAuth must be used within AuthProvider');
  return context;
};

// Customer dashboards should feel persistent on phones while Firebase tokens
// and server-side session-limit checks still enforce account control.
const INACTIVITY_TIMEOUT_MS = 7 * 24 * 60 * 60 * 1000;

// Company discovery runs in the background, so it can wait for login() to store the server session.
// The 3 s default lost that race on slow links (Brent 2026-10-12: session landed 3.2 s after sign-in),
// which hid the account switcher until a page reload.
const DISCOVERY_SESSION_WAIT_MS = 20000;

// Internal lab/test orgs stay visible in Developer Console management pages,
// but should not appear in the developer org switcher used for customer views.
const HIDDEN_DEVELOPER_SELECTOR_ORG_IDS = new Set([
  'org_1772052465562_zjc6ajbka',
  'org_1779154367498_yht1dl5oi',
  'org_1781208364291_nk0bul5t5',
  'org_1781208431264_erwgnrxzh',
  'org_1781409960613_ik3zs07ly',
  'org_labtest'
]);

// Billing step 6: a viewer company on the new pricing needs its own subscription (started on its home's Billing page),
// not the old per-wash shared-access checkout.
function viewerNeedsSubscription(error) {
  return error?.code === 'COMPANY_ACCESS_PAYMENT_REQUIRED' && error?.entitlement?.source === 'rate_sheet';
}

export const AuthProvider = ({ children }) => {
  const [currentUser, setCurrentUser] = useState(null);
  const [userData, setUserData] = useState(null);
  const [organization, setOrganization] = useState(null);
  const [loading, setLoading] = useState(true);
  const [supportOrgs, setSupportOrgs] = useState([]); // Orgs support user has access to
  const [selectedSupportOrg, setSelectedSupportOrg] = useState(null); // Currently selected org for support user
  const [supportAccess, setSupportAccess] = useState(null); // AC1: the support login's level (label, canChange, historyDays)
  const [allOrganizations, setAllOrganizations] = useState([]); // All orgs for developer
  const [selectedDeveloperOrg, setSelectedDeveloperOrg] = useState(null); // Currently selected org for developer
  const [companyOrganizations, setCompanyOrganizations] = useState([]);
  // SH9: which business's sites the location dropdown shows (a group within the current account), or null = all
  const [selectedSiteGroup, setSelectedSiteGroup] = useState(null);
  const [companyView, setCompanyView] = useState(false);
  const [companyAccessError, setCompanyAccessError] = useState(false);
  const authEpoch = useRef(0);
  const inactivityTimer = useRef(null);
  const previousUserUid = useRef(null);

  useEffect(() => {
    // Listen for auth state changes AND token changes
    const unsubscribe = onIdTokenChanged(auth, async (user) => {
      // Firebase emits this callback for hourly token refreshes as well as for
      // identity changes. A refreshed token for the same UID must not rerun
      // the full profile/company bootstrap: doing so replays polling requests
      // and can turn a transient company-access 401 into a request storm.
      if (user && previousUserUid.current === user.uid) {
        setCurrentUser(user);
        return;
      }

      const epoch = ++authEpoch.current;
      if (user) {
        // Never render a new identity with the previous account's profile while loading.
        if (previousUserUid.current !== user.uid) {
          setLoading(true);
          setUserData(null);
          setOrganization(null);
          setFrozenDate(null);
          setSupportOrgs([]); setSupportAccess(null);
          setSelectedSupportOrg(null);
          setAllOrganizations([]);
          setSelectedDeveloperOrg(null);
          setCompanyView(false);
          setCompanyAccessError(false);
          setCompanyOrganizations([]);
        }
        const priorUid = previousUserUid.current || sessionStorage.getItem('lastAuthenticatedUid');
        if (priorUid && priorUid !== user.uid) {
          for (const key of ['developerSelectedOrgId', 'supportSelectedOrgId', 'impersonation_session', 'viewAsSession']) {
            sessionStorage.removeItem(key);
          }
          localStorage.removeItem('selectedOrgId');
          localStorage.removeItem('selectedLocationId');
        }
        // Detect user change (different user than before, or new session after window close)
        if (previousUserUid.current && previousUserUid.current !== user.uid) {
          setCompanyView(false);
          setCompanyAccessError(false);
          setCompanyOrganizations([]);
          clearApiCache();
        } else if (!previousUserUid.current) {
          // First load - check if there's a stored previous user that differs
          const storedPreviousUid = sessionStorage.getItem('lastAuthenticatedUid');
          if (storedPreviousUid && storedPreviousUid !== user.uid) {

            clearApiCache();
          }
        }

        // Store current user for future comparison
        previousUserUid.current = user.uid;
        sessionStorage.setItem('lastAuthenticatedUid', user.uid);

        setCurrentUser(user);

        // Get user data from Firestore
        try {
          // Developer View as: this tab shows one customer login's dashboard; its profile comes from the API session.
          let viewAsProfile = null;
          if (isViewingAs()) {
            try {
              const current = (await axiosInstance.get('/developer/view-as/current',
                { skipViewAs: true, skipCompanyScope: true, skipAutoLocation: true })).data;
              if (current?.active && current.profile?.uid === viewAsSession()?.uid) viewAsProfile = current.profile;
            } catch (error) { viewAsProfile = null; }
            if (epoch !== authEpoch.current) return;
            if (!viewAsProfile) forgetViewAs();
          }
          const userDoc = viewAsProfile
            ? { exists: () => true, data: () => ({ ...viewAsProfile }) }
            : await getDoc(doc(db, 'users', user.uid));
          if (epoch !== authEpoch.current) return;
          if (userDoc.exists()) {
            const data = userDoc.data();
            let companyContext = null;
            let discovery = null;
            if (companyDashboardEnabled()) {
              const selection = companySelection(user.uid);
              if (selection) {
                setLoading(true);
                try {
                  companyContext = await fetchCompanyAccess(user, selection.orgId, { retries: 4 });
                  const options = await fetchCompanyAccess(user, undefined, { retries: 4 });
                  if (epoch !== authEpoch.current) return;
                  if (!options.organizations?.some(org => org.id === selection.orgId)) throw new Error('Access unavailable');
                  setCompanyOrganizations(options.organizations);
                  Object.assign(data, companyContext.user, { isDemo: false, dencarIntegration: null });
                  setCompanyView(true);
                  setCompanyAccessError(false);
                  setOrganization(companyContext.organization);
                  setFrozenDate(companyContext.organization.frozenDate || null);
                } catch (error) {
                  if (epoch !== authEpoch.current) return;
                  if (isSessionEvicted(error)) return signOutEvictedSession();
                  setUserData(null);
                  setOrganization(null);
                  setCompanyAccessError(error);
                  setLoading(false);
                  return;
                }
              } else if (['owner', 'admin', 'manager'].includes(data.role) && !data.isSupportAccount) {
                // Company discovery is optional for the normal dashboard. Run
                // it after auth bootstrap so it cannot delay first render.
                discovery = fetchCompanyAccess(user, undefined, { retries: 1, sessionWaitMs: DISCOVERY_SESSION_WAIT_MS })
                  .then(options => {
                    if (epoch !== authEpoch.current) return 'stale';
                    const target = autoOpenTarget(user.uid, options);
                    if (target) {
                      prepareCompanySwitch(user.uid, target, false, clearApiCache);
                      // Close-out #3: keep the page the person asked for (a bookmark or an alert-email link)
                      const here = `${window.location.pathname || '/'}${window.location.search || ''}`;
                      window.location.assign(['/', '/login'].includes(window.location.pathname || '/') ? '/dashboard' : here);
                      return 'redirect';
                    }
                    setCompanyOrganizations(options.organizations || []);
                    return 'stay';
                  })
                  .catch(error => {
                    if (epoch !== authEpoch.current) return 'stale';
                    if (isSessionEvicted(error)) { signOutEvictedSession(); return 'redirect'; }
                    setCompanyOrganizations([]);
                    return 'stay';
                  });
              }
            }

            // AC1: Car Wash Minder support login. The accounts it may open come from the API (each account's
            // "Car Wash Minder support" switch), and an opened account is loaded through the API as well.
            let supportContext = null;
            if (data.isSupportAccount || data.role === 'support') {
              let list = { accounts: [] };
              try { list = (await axiosInstance.get('/support-access/accounts')).data; } catch (e) { list = { accounts: [] }; }
              if (epoch !== authEpoch.current) return;
              setSupportOrgs(list.accounts || []);
              setSupportAccess(list.level ? { level: list.level, label: list.label, canChange: list.canChange,
                historyDays: list.historyDays } : null);
              const storedSupportOrgId = sessionStorage.getItem('supportSelectedOrgId');
              const storedOrg = storedSupportOrgId
                ? (list.accounts || []).find(o => o.orgId === storedSupportOrgId && o.allowed)
                : null;
              if (storedOrg) {
                try {
                  supportContext = (await axiosInstance.get('/support-access/context', { params: { orgId: storedOrg.orgId } })).data;
                } catch (e) { supportContext = null; }
                if (epoch !== authEpoch.current) return;
              }
              if (!supportContext) {
                sessionStorage.removeItem('supportSelectedOrgId');
                setSelectedSupportOrg(null);
                setUserData(data);
                setLoading(false);
                return; // the picker page lists the accounts
              }
              setSelectedSupportOrg(storedOrg);
              data.orgId = storedOrg.orgId;
              data.orgName = storedOrg.orgName;
              data.role = 'admin'; // acts as an admin within the support limits the API enforces
              setOrganization(supportContext.organization);
              setFrozenDate(supportContext.organization.frozenDate || null);
            }

            // Check if this is a developer account - they can select any org
            if (data.role === 'developer') {


              // Check if developer has already selected an org (stored in sessionStorage)
              const storedDevOrgId = sessionStorage.getItem('developerSelectedOrgId');
              if (storedDevOrgId) {

                // We'll load the org name later when we fetch all orgs
                setSelectedDeveloperOrg({ orgId: storedDevOrgId });
                data.orgId = storedDevOrgId;
              }
              // Developer keeps their 'developer' role - don't change to admin
              // The backend handles developer permissions
            }

            setUserData(data);

            // Load organization and Dencar data in PARALLEL for faster login
            const parallelFetches = [];

            // Queue org fetch if user has orgId
            if (data.orgId && !companyContext && !supportContext) {
              parallelFetches.push(
                getDoc(doc(db, 'organizations', data.orgId))
                  .then(orgDoc => ({ type: 'org', doc: orgDoc }))
                  .catch(err => ({ type: 'org', error: err }))
              );
            }

            // Queue Dencar fetch if user is in org_001
            if (data.orgId === 'org_001' && !viewAsProfile) {
              parallelFetches.push(
                getDoc(doc(db, 'users', user.uid, 'integrations', 'dencar'))
                  .then(dencarDoc => ({ type: 'dencar', doc: dencarDoc }))
                  .catch(err => ({ type: 'dencar', error: err }))
              );
            }

            // Execute all fetches in parallel
            if (parallelFetches.length > 0) {
              const results = await Promise.all(parallelFetches);
              if (epoch !== authEpoch.current) return;

              for (const result of results) {
                if (result.type === 'org') {
                  if (result.error) {
                    console.error('[Auth] Error loading organization data:', result.error);
                  } else if (result.doc.exists()) {
                    const orgData = result.doc.data();
                    // Close-out #3: a company home has no sites of its own; wait for company discovery (it may open
                    // the one place this person runs) so the empty account never flashes up first.
                    if (orgData.homeOfCompanyId && discovery && (await discovery) !== 'stay') return;
                    setOrganization({
                      id: result.doc.id,
                      ...orgData
                    });
                    setFrozenDate(orgData.frozenDate || null);
                  } else {
                    console.warn('[Auth] No organization document found for:', data.orgId);
                  }
                } else if (result.type === 'dencar') {
                  if (result.error) {

                  } else if (result.doc.exists()) {
                    const dencarData = result.doc.data();
                    data.dencarIntegration = {
                      enabled: dencarData.enabled || false,
                      username: dencarData.username || null
                    };

                  }
                }
              }
            }

            // Developer access is decided from the Firestore profile, not token
            // claims, so no forced refresh is needed here either; one would only
            // re-run this bootstrap (a third time at login).
          } else {
            console.error('[Auth] No user document found for:', user.uid);
            if (companyDashboardEnabled() && companySelection(user.uid)) setCompanyAccessError(true);
          }
        } catch (error) {
          if (epoch !== authEpoch.current) return;
          console.error('[Auth] Error loading user data:', error);
          if (companyDashboardEnabled() && sessionStorage.getItem('companySelectedWash')) {
            setCompanyAccessError(true);
          }
        }

        if (epoch !== authEpoch.current) return;

        // No forced token refresh timer. The Firebase SDK refreshes ID tokens
        // on its own before they expire and getIdToken() at request time
        // returns the current one. A forced getIdToken(true) here fired
        // onIdTokenChanged every 55 minutes for no benefit; the API reads no
        // token claims beyond identity (2026-09-22 incident, item I).

        // Set up inactivity auto-logout
        const resetInactivityTimer = () => {
          if (inactivityTimer.current) {
            clearTimeout(inactivityTimer.current);
          }
          inactivityTimer.current = setTimeout(async () => {

            clearApiCache();
            await signOut(auth);
            window.location.reload();
          }, INACTIVITY_TIMEOUT_MS);
        };

        // Track user activity
        const activityEvents = ['mousedown', 'keydown', 'touchstart', 'scroll'];
        activityEvents.forEach(event => {
          window.addEventListener(event, resetInactivityTimer, { passive: true });
        });

        // Start the inactivity timer
        resetInactivityTimer();

        // Store cleanup function for activity listeners
        user._activityCleanup = () => {
          activityEvents.forEach(event => {
            window.removeEventListener(event, resetInactivityTimer);
          });
          if (inactivityTimer.current) {
            clearTimeout(inactivityTimer.current);
            inactivityTimer.current = null;
          }
        };
      } else {
        setCompanyOrganizations([]);
        setCompanyView(false);
        setCompanyAccessError(false);
        clearCompanySelection();
        clearSiteGroup();

        setCurrentUser(null);
        setUserData(null);
        setOrganization(null);
        setFrozenDate(null);
        if (inactivityTimer.current) {
          clearTimeout(inactivityTimer.current);
          inactivityTimer.current = null;
        }
      }
      setLoading(false);
    });

    return () => {
      unsubscribe();
      if (inactivityTimer.current) {
        clearTimeout(inactivityTimer.current);
      }
    };
  }, []);

  useEffect(() => {
    if (!companyView || !currentUser) return undefined;
    let cancelled = false;
    const deny = (error = true) => { if (!cancelled) setCompanyAccessError(error); };
    const revalidate = async () => {
      try {
        const selection = companySelection(currentUser.uid);
        if (!selection) throw new Error('Selection missing');
        await fetchCompanyAccess(currentUser, selection.orgId);
      } catch (error) {
        if (isSessionEvicted(error)) return signOutEvictedSession();
        deny(error);
      }
    };
    window.addEventListener('company-access-denied', deny);
    window.addEventListener('focus', revalidate);
    const timer = setInterval(revalidate, 30000);
    return () => {
      cancelled = true;
      clearInterval(timer);
      window.removeEventListener('company-access-denied', deny);
      window.removeEventListener('focus', revalidate);
    };
  }, [companyView, currentUser]);

  const selectCompanyOrg = useCallback((orgId) => {
    const choice = companyOrganizations.find(org => org.id === orgId);
    if (!choice || !currentUser) return;
    prepareCompanySwitch(currentUser.uid, choice.id, choice.direct === true, clearApiCache);
    window.location.assign('/dashboard');
  }, [companyOrganizations, currentUser]);

  // SH9: the switcher's entries (each business that runs reachable sites) and choosing one. A group inside the account
  // already open only filters the location dropdown; another account switches as before.
  const companyEntries = useMemo(() => buildCompanyEntries(companyOrganizations), [companyOrganizations]);
  useEffect(() => { setSelectedSiteGroup(currentUser ? readSiteGroup(currentUser.uid) : null); }, [currentUser?.uid]);
  const selectCompanyEntry = useCallback((entry) => {
    if (!entry || !currentUser) return;
    writeSiteGroup(currentUser.uid, entry);
    if (entry.orgId === userData?.orgId) {
      setSelectedSiteGroup(readSiteGroup(currentUser.uid));
      return;
    }
    selectCompanyOrg(entry.orgId);
  }, [currentUser, userData?.orgId, selectCompanyOrg]);

  const login = useCallback(async (email, password) => {
    try {
      clearCompanySelection();
      clearSiteGroup();
      const result = await signInWithEmailAndPassword(auth, email, password);

      // Create server session (two-session limit enforcement)
      // This generates a sessionId and stores it; the backend keeps the newest two.
      try {
        const token = await result.user.getIdToken();
        const API_URL = process.env.REACT_APP_API_URL || 'https://chemical-monitor-api-57459833175.us-central1.run.app/api';
        const response = await fetch(`${API_URL}/auth/session`, {
          method: 'POST',
          headers: {
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'application/json'
          }
        });

        if (response.ok) {
          const { sessionId } = await response.json();
          sessionStorage.setItem('sessionId', sessionId);
          // No forced token refresh here: it would fire onIdTokenChanged and run
          // the whole bootstrap a second time. Company discovery already waits
          // for this session id (waitForServerSession).
        } else {
          console.warn('[Auth] Failed to create session - continuing without session enforcement');
        }
      } catch (sessionError) {
        // Don't fail login if session creation fails
        console.warn('[Auth] Session creation error:', sessionError.message);
      }

      return result;
    } catch (error) {
      console.error('[Auth] Login failed:', error);
      throw error;
    }
  }, []);

  const logout = useCallback(async () => {
    if (isViewingAs()) {
      // End the View as session too, or the developer's login stays read-only until it runs out.
      try { await axiosInstance.post('/developer/view-as/stop', {}, { skipViewAs: true, skipCompanyScope: true }); } catch (e) { /* runs out by itself */ }
      forgetViewAs();
    }
    previousUserUid.current = null;
    sessionStorage.removeItem('lastAuthenticatedUid');
    sessionStorage.removeItem('supportSelectedOrgId'); // Clear support user org selection
    sessionStorage.removeItem('developerSelectedOrgId'); // Clear developer org selection
    sessionStorage.removeItem('impersonation_session');
    sessionStorage.removeItem('sessionId'); // Clear session for session-limit enforcement
    clearCompanySelection();
    clearSiteGroup();
    setSupportOrgs([]); setSupportAccess(null);
    setSelectedSupportOrg(null);
    setAllOrganizations([]);
    setSelectedDeveloperOrg(null);
    clearApiCache(); // Clear cached API data to prevent stale data on next login
    await signOut(auth);
  }, []);

  // Select an organization for support user
  const selectSupportOrg = useCallback(async (orgId) => {
    const org = supportOrgs.find(o => o.orgId === orgId && o.allowed);
    if (!org) {
      console.error('[Auth] Account not available to support:', orgId);
      return false;
    }
    // AC1: open through the API (it checks the account's support switch), then reload into that account
    try {
      await axiosInstance.get('/support-access/context', { params: { orgId } });
    } catch (error) {
      console.error('[Auth] Could not open account for support:', error);
      return false;
    }
    clearApiCache();
    sessionStorage.setItem('supportSelectedOrgId', orgId);
    window.location.assign('/dashboard');
    return true;
  }, [supportOrgs]);

  // Switch to a different org (for support users)
  const switchSupportOrg = useCallback(() => {

    sessionStorage.removeItem('supportSelectedOrgId');
    setSelectedSupportOrg(null);
    setOrganization(null);
    clearApiCache();
    // Don't clear userData - keep isSupportAccount and supportOrgs
  }, []);

  // Load all organizations for developer
  const loadAllOrganizations = useCallback(async () => {
    if (userData?.role !== 'developer') return;
    const epoch = authEpoch.current;

    try {
      const token = await currentUser.getIdToken();
      if (epoch !== authEpoch.current) return;
      const response = await fetch(`${process.env.REACT_APP_API_URL}/developer/organizations`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      if (response.ok) {
        const orgs = await response.json();
        if (epoch !== authEpoch.current) return;
        const visibleOrgs = Array.isArray(orgs)
          ? sortByName(orgs.filter(org => !HIDDEN_DEVELOPER_SELECTOR_ORG_IDS.has(org.id)))
          : [];

        setAllOrganizations(visibleOrgs);


        // If we had a stored org selection, update it with full org data
        const storedDevOrgId = sessionStorage.getItem('developerSelectedOrgId');
        if (storedDevOrgId) {
          const storedOrg = visibleOrgs.find(o => o.id === storedDevOrgId);
          if (storedOrg) {
            setSelectedDeveloperOrg({ orgId: storedOrg.id, orgName: storedOrg.name });
          } else {
            sessionStorage.removeItem('developerSelectedOrgId');
            setSelectedDeveloperOrg(null);
            setOrganization(null);
            setFrozenDate(null);
            setUserData(prev => {
              if (!prev || prev.role !== 'developer' || prev.orgId !== storedDevOrgId) return prev;
              const { orgId, orgName, ...rest } = prev;
              return rest;
            });
            clearApiCache();
          }
        }
      }
    } catch (error) {
      console.error('[Auth] Error loading organizations for developer:', error);
    }
  }, [userData?.role, currentUser]);

  // Select an organization for developer
  // This makes the developer appear exactly like an admin of that org
  const selectDeveloperOrg = useCallback(async (orgId, orgName) => {
    const epoch = authEpoch.current;
    sessionStorage.setItem('developerSelectedOrgId', orgId);
    setSelectedDeveloperOrg({ orgId, orgName });

    // Clear API cache FIRST to ensure fresh data for new org
    clearApiCache();

    // Load the organization data
    try {
      const orgDoc = await getDoc(doc(db, 'organizations', orgId));
      if (epoch !== authEpoch.current) return;
      if (orgDoc.exists()) {
        const orgData = orgDoc.data();
        setOrganization({
          id: orgDoc.id,
          ...orgData
        });
        setFrozenDate(orgData.frozenDate || null);


        // Update userData with the selected org (keep developer role)
        // This makes the developer appear as part of this org
        setUserData(prev => ({
          ...prev,
          orgId: orgId,
          orgName: orgData.name
        }));
      } else {
        console.error('[Auth] Organization document does not exist:', orgId);
      }
    } catch (error) {
      console.error('[Auth] Error loading org for developer:', error);
    }
  }, []);

  // Compute role flags based on capabilities
  const userRole = userData?.role || '';

  // Capability-based permissions (owner has same access as admin)
  const canManageUsers = ['developer', 'superuser', 'owner', 'admin'].includes(userRole);
  const canManageOrganization = ['developer', 'superuser', 'owner', 'admin'].includes(userRole);
  const canConfigureBarrels = ['developer', 'superuser', 'owner', 'admin', 'manager'].includes(userRole);
  const canViewAuditLogs = ['developer', 'superuser', 'owner', 'admin'].includes(userRole);
  const canAccessDeveloperConsole = userRole === 'developer';
  const canImpersonate = userRole === 'developer';

  // Legacy role flags (kept for backward compatibility, will phase out)
  const isDeveloperUser = userRole === 'developer';
  const isSuperuserUser = userRole === 'superuser';
  const isOwnerUser = userRole === 'owner';
  const isAdminUser = ['developer', 'superuser', 'owner', 'admin'].includes(userRole);

  // Feature-based permission helpers
  const hasFeatureAccess = useCallback((feature) => {
    // Developers and superusers bypass all checks
    if (['developer', 'superuser'].includes(userRole)) return true;

    // Check if organization has feature enabled
    if (!organization || !organization.features) return false;
    return organization.features[feature] === true;
  }, [userRole, organization]);

  const hasPermission = useCallback((feature, action) => {
    // Developers and superusers have all permissions
    if (['developer', 'superuser'].includes(userRole)) return true;

    // Check user's specific permission
    if (!userData || !userData.permissions) return false;
    return userData.permissions[feature]?.[action] === true;
  }, [userRole, userData]);

  // Demo mode is now a USER-level concept, not organization-level
  // This allows org_001 to behave like any other organization
  const isDemoUser = useCallback(() => {
    return userData?.isDemo === true;
  }, [userData]);

  // Keep isDemoOrg for backwards compatibility, but prefer isDemoUser
  const isDemoOrg = useCallback(() => {
    return organization?.type === 'demo';
  }, [organization]);

  const isProductionOrg = useCallback(() => {
    return organization?.type === 'production';
  }, [organization]);

  // Memoize the context value to prevent unnecessary re-renders of consumers
  const value = useMemo(() => ({
    currentUser,
    userData,
    user: userData, // Add user alias for userData
    organization,
    login,
    logout,
    loading,
    companyOrganizations,
    companyView,
    selectCompanyOrg,
    companyEntries,
    selectCompanyEntry,
    selectedSiteGroup,
    isAuthenticated: !!currentUser,
    // Support user properties
    isSupportAccount: userData?.isSupportAccount || false,
    supportOrgs,
    selectedSupportOrg,
    selectSupportOrg,
    switchSupportOrg,
    needsOrgSelection: (userData?.isSupportAccount || userData?.role === 'support') && !selectedSupportOrg,
    // AC1: a support login that has not opened an account yet - data loaders wait (the API would refuse them)
    supportPicking: !!userData && (userData.isSupportAccount === true || userData.role === 'support') && !selectedSupportOrg,
    supportAccess,
    // Developer properties
    allOrganizations,
    selectedDeveloperOrg,
    selectDeveloperOrg,
    loadAllOrganizations,
    // Legacy role flags
    isSuperuser: isSuperuserUser,
    isOwner: isOwnerUser,
    isAdmin: isAdminUser,
    isDeveloper: isDeveloperUser,
    userRole, // Expose the raw role
    // New capability-based permissions
    capabilities: {
      canManageUsers,
      canManageOrganization,
      canConfigureBarrels,
      canViewAuditLogs,
      canAccessDeveloperConsole,
      canImpersonate
    },
    // Helper function for checking capabilities
    can: (capability) => {
      const capMap = {
        manageUsers: canManageUsers,
        manageOrganization: canManageOrganization,
        configureBarrels: canConfigureBarrels,
        viewAuditLogs: canViewAuditLogs,
        accessDeveloperConsole: canAccessDeveloperConsole,
        impersonate: canImpersonate
      };
      return capMap[capability] || false;
    },
    // Feature-based access control
    hasFeatureAccess,
    hasPermission,
    isDemoUser,
    isDemoOrg,
    isProductionOrg,
    // Organization features shortcuts
    features: organization?.features || {
      dencar: false,
      chemicalMonitoring: false,
      sensors: false,
      alerts: false
    },
    // User permissions shortcuts
    permissions: userData?.permissions || {
      dencar: { view: false, configure: false, downloadData: false },
      chemicalMonitoring: { view: false, configure: false, refillBarrels: false },
      billing: { view: false, manage: false },
      users: { view: false, invite: false, remove: false }
    },
    orgId: userData?.orgId,
    getAuthHeaders: async () => {
      if (!currentUser) throw new Error('No user logged in');
      const token = await currentUser.getIdToken();
      return {
        'Authorization': `Bearer ${token}`,
        ...companyScopeHeaders(currentUser.uid),
        ...(companyView && sessionStorage.getItem('sessionId') ? { 'x-session-id': sessionStorage.getItem('sessionId') } : {}),
        // AC1: fetch()-based loaders (e.g. locations) need the opened account too
        ...(supportSessionOrgId() ? { 'x-support-org-id': supportSessionOrgId() } : {}),
        // Developer View as: fetch()-based loaders answer as the viewed login too
        ...viewAsHeaders()
      };
    }
  }), [
    currentUser, userData, organization, loading, supportOrgs, selectedSupportOrg, supportAccess,
    companyOrganizations, companyView, selectCompanyOrg, companyEntries, selectCompanyEntry, selectedSiteGroup,
    allOrganizations, selectedDeveloperOrg, login, logout, selectSupportOrg,
    switchSupportOrg, selectDeveloperOrg, loadAllOrganizations,
    isSuperuserUser, isOwnerUser, isAdminUser, isDeveloperUser, userRole,
    canManageUsers, canManageOrganization, canConfigureBarrels,
    canViewAuditLogs, canAccessDeveloperConsole, canImpersonate,
    hasFeatureAccess, hasPermission, isDemoUser, isDemoOrg, isProductionOrg
  ]);

  return (
    <AuthContext.Provider value={value}>
      {companyAccessError ? <div role="alert" style={{ padding: 24 }}>
        {viewerNeedsSubscription(companyAccessError) ? <>
          <p><strong>{companyAccessError.entitlement.status === 'paused'
            ? "Your company's subscription has ended." : 'Your company needs its own subscription to see this location.'}</strong></p>
          <p>Everyone who sees a location has a Car Wash Minder subscription. An admin of your company can start it
            from your company&apos;s Billing page; this location opens as soon as it is paid.</p>
          <button onClick={() => {
            prepareCompanySwitch(currentUser.uid, 'home', true, clearApiCache);
            window.location.assign('/admin/billing');
          }}>Start subscription</button>{' '}
        </> : <p>Access to this wash could not be verified.</p>}
        <button onClick={() => window.location.reload()}>Retry</button>{' '}
        <button onClick={() => {
          prepareCompanySwitch(currentUser.uid, 'home', true, clearApiCache);
          window.location.assign('/dashboard');
        }}>Return to my wash</button>{' '}
        <button onClick={logout}>Logout</button>
      </div> : !loading && children}
    </AuthContext.Provider>
  );
};
